import React, { FC, useContext, useEffect, useState } from "react";
import { BusinessHomeProfile } from "../../../models/businessHomeProfile";
import { Controller, useForm } from "react-hook-form";
import { MobXProviderContext } from "mobx-react";
import { extractLocationDetails } from "../../../utils/stringUtils";
import { normalizePlainText } from "../../../utils/securityUtils";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import SearchAddress from "../../../components/general/SearchAddress";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

type EditHomeProfileModalProps = {
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  addFlag: boolean;
  setAddFlag: (addFlag: boolean) => void;
  homeDetails: BusinessHomeProfile | null;
  showArchiveAction?: boolean;
  onArchive?: () => void;
};

const EditHomeProfileModal: FC<EditHomeProfileModalProps> = ({
  isOpen,
  toggleModal,
  addFlag,
  setAddFlag,
  homeDetails,
  showArchiveAction = false,
  onArchive,
}) => {
  const toast = React.useRef<any>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [businessAddress, setBusinessAddress] = useState<string>("");
  const [addressLine1, setAddressLine1] = useState<string>("");
  const [addressLine2, setAddressLine2] = useState<string>("");
  const [formattedAddress, setFormattedAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [latitude, setLatitude] = useState<string>("");
  const [longitude, setLongitude] = useState<string>("");

  const defaultValues = {
    businessName: '',
    facilityType: '',
    businessAddress: '',
    businessPhoneNumber: '',
    businessFaxNumber: '',
  }

  const { control, formState: { errors }, handleSubmit, reset, setValue } = useForm({ defaultValues });

  useEffect(() => {
    if (homeDetails) {
      reset({
        businessName: homeDetails.businessName || "",
        facilityType: homeDetails.facilityType || authExperience.providerSignup?.defaultFacilityType || "afh",
        businessAddress: homeDetails.businessAddressLine1 || "",
        businessPhoneNumber: homeDetails.businessPhoneNumber || "",
        businessFaxNumber: homeDetails.businessFaxNumber || "",
      });
      setBusinessAddress(homeDetails.businessAddressLine1 || "");
      setAddressLine1(homeDetails.businessAddressLine1 || "");
      setAddressLine2(homeDetails.businessAddressLine2 || "");
      setFormattedAddress(homeDetails.formattedAddress || "");
      setCity(homeDetails.city || "");
      setState(homeDetails.state || "");
      setCountry(homeDetails.country || "");
      setPostalCode(homeDetails.zipcode || "");
      setLatitude(homeDetails.latitude || "");
      setLongitude(homeDetails.longitude || "");
    }
  }, [homeDetails, reset]);

  const onSubmit = async (formDataOnly: any) => {
    const updatedData = {
      ...homeDetails,
      businessName: normalizePlainText(formDataOnly.businessName, { maxLength: 30 }),
      facilityType: String(formDataOnly.facilityType || authExperience.providerSignup?.defaultFacilityType || "afh").trim().toLowerCase(),
      businessPhoneNumber: normalizePlainText(formDataOnly.businessPhoneNumber, { maxLength: 15 }),
      businessFaxNumber: normalizePlainText(formDataOnly.businessFaxNumber, { maxLength: 30 }),
      businessAddressLine1: addressLine1,
      businessAddressLine2: addressLine2,
      businessAddress: businessAddress,
      formattedAddress: formattedAddress,
      city: city,
      state: state,
      country: country,
      zipcode: postalCode,
      latitude: latitude.toString(),
      longitude: longitude.toString(),
    } as BusinessHomeProfile;

    console.log("Updated Data: ", updatedData);
    setLoading(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.updateHome(updatedData);
      if (response) {
        reset();
        toggleModal(false);
        setAddFlag(!addFlag);
      }
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const handleAddressChange = (e) => {
    const place = e.target?.place;
    console.log("Place selected: ", place);
    if (place) {
      const details = extractLocationDetails(place);
      setAddressLine1(details.addressLine1 || "");
      setAddressLine2(details.addressLine2 || "");
      setFormattedAddress(place.formatted_address || "");
      setCity(details.city || "");
      setState(details.state || "");
      setCountry(details.country || "");
      setPostalCode(details.postalCode || "");
      setLatitude(place.geometry?.location?.lat() || "");
      setLongitude(place.geometry?.location?.lng() || "");
      setBusinessAddress(place.formatted_address || "");
      setValue("businessAddress", place.formatted_address || "", { shouldValidate: true });
    } else {
      setValue("businessAddress", '', { shouldValidate: true });
    }
  };

  const headerElement = renderSurfaceHeader(
    "Edit Home Profile",
    "Adjust the core address and contact details for this home.",
    "pi pi-home",
  );

  const footerContent = (
    <div className="flex flex-column gap-3">
      {errorMessage ? <div className="p-error">{errorMessage}</div> : null}
      <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
        <div>
          {showArchiveAction && onArchive ? (
            <Button
              label="Archive"
              severity="danger"
              outlined
              rounded
              icon="pi pi-archive"
              onClick={onArchive}
              disabled={loading}
            />
          ) : null}
        </div>
        <div className="flex justify-content-end gap-2">
          <Button label="Close" severity="secondary" outlined onClick={() => toggleModal(false)} disabled={loading} />
          <Button label="Save Changes" severity="warning" rounded loading={loading} onClick={handleSubmit(onSubmit)} />
        </div>
      </div>
    </div>
  );

  return (
    <Dialog visible={isOpen} modal header={headerElement} footer={footerContent} style={appDialogStyle}
      headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
      onHide={() => { if (!isOpen) return; toggleModal(false); }}>
      <form>
        <Toast ref={toast} />
        <div style={appAccentPanelStyle} className="mb-3">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>{authExperience.providerHome.profileSectionTitle}</div>
          <div className="mt-2" style={appMutedTextStyle}>
            {authExperience.providerHome.profileSectionSubtitle}
          </div>
        </div>
        <div style={appFormSectionStyle}>
          <div className="field">
            <span className="p-float-label">
              <Controller name="businessName" control={control} rules={{ required: authExperience.providerHome.homeNameRequiredMessage, maxLength: { value: 30, message: "Home name must be at most 30 characters long." } }} render={({ field, fieldState }) => (
                <InputText id={field.name} {...field} maxLength={30} className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
              <label htmlFor="businessName" className={classNames({ 'p-error': errors.businessName })}>{authExperience.providerHome.homeNameLabel}</label>
            </span>
            {getFormErrorMessage('businessName')}
          </div>
          <div className="field">
            <label className="mb-2 block">{authExperience.providerHome.facilityTypeLabel}</label>
            <Controller
              name="facilityType"
              control={control}
              rules={{ required: "Please select a facility type." }}
              render={({ field }) => (
                <Dropdown
                  id={field.name}
                  value={field.value}
                  options={authExperience.facilityTypeOptions || []}
                  optionLabel="label"
                  optionValue="value"
                  disabled
                  placeholder="Select facility type"
                  className="w-full"
                />
              )}
            />
            {getFormErrorMessage('facilityType')}
          </div>
          <div className="field">
            <label className="mb-2 block">{authExperience.providerHome.homeAddressLabel}</label>
            <span className="p-float-label">
              <Controller name="businessAddress" control={control} rules={{ required: 'Please select your full address from the Google suggestions.' }} render={({ field, fieldState }) => (
                <SearchAddress id={field.name} {...field}
                  onChange={handleAddressChange}
                  className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
            </span>
            {getFormErrorMessage('businessAddress')}
          </div>
          <div className="field mb-0">
            <span className="p-float-label">
              <Controller name="businessPhoneNumber" control={control} rules={{
                required: 'Phone number is required.',
                pattern: { value: /^[0-9]*$/, message: 'Phone number must be numeric.' },
                maxLength: { value: 15, message: 'Phone number must be at most 15 characters long.' }
              }} render={({ field, fieldState }) => (
                <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
              <label htmlFor="businessPhoneNumber" className={classNames({ 'p-error': errors.businessPhoneNumber })}>Phone</label>
            </span>
            {getFormErrorMessage('businessPhoneNumber')}
          </div>
          <div className="field mb-0" style={{ marginTop: "1.5rem" }}>
            <span className="p-float-label">
              <Controller name="businessFaxNumber" control={control} rules={{
                required: 'Fax number is required.',
                pattern: { value: /^[0-9+()\-.\s]+$/, message: 'Enter a valid fax number.' },
                maxLength: { value: 30, message: 'Fax number must be at most 30 characters long.' }
              }} render={({ field, fieldState }) => (
                <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
              <label htmlFor="businessFaxNumber" className={classNames({ 'p-error': errors.businessFaxNumber })}>Fax</label>
            </span>
            {getFormErrorMessage('businessFaxNumber')}
          </div>
        </div>
      </form>
    </Dialog>
  );
};

export default EditHomeProfileModal;
